import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, IconButton, Chip,
  TextField, Switch, FormControlLabel, Snackbar, Alert,
  CircularProgress, Tooltip, TableSortLabel
} from '@mui/material';
import { Add, Edit, Delete, Refresh, Extension as ExtensionIcon } from '@mui/icons-material';
import axios from 'axios';
import { extractArrayFromResponse } from '../utils/apiHelpers';
import { ColumnToggle } from './common/ColumnToggle';
import { Modal } from './common/Modal';

interface ExtraProgram {
  id: number;
  name: string;
  rate: number;
  is_active: boolean | number;
}

type Order = 'asc' | 'desc';
type SortableKey = 'id' | 'name' | 'rate' | 'is_active';

const toBoolean = (value: boolean | number | undefined): boolean => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  return false;
};

const ExtraProgramsPage: React.FC = () => {
  const [programs, setPrograms] = useState<ExtraProgram[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingProgram, setEditingProgram] = useState<ExtraProgram | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [formData, setFormData] = useState({
    name: '',
    rate: 0,
    is_active: true
  });

  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<SortableKey>('id');

  const columns = [
    { key: 'id', label: 'ID', defaultVisible: true },
    { key: 'name', label: 'Название', defaultVisible: true },
    { key: 'rate', label: 'Ставка', defaultVisible: true },
    { key: 'is_active', label: 'Статус', defaultVisible: true },
  ];

  const [visibleColumns, setVisibleColumns] = useState<string[]>([]);

  useEffect(() => {
    const saved = localStorage.getItem('extra_programs_visible_columns');
    if (saved) {
      try {
        setVisibleColumns(JSON.parse(saved));
        return;
      } catch (e) {}
    }
    setVisibleColumns(columns.filter(c => c.defaultVisible).map(c => c.key));
  }, []);

  const loadPrograms = async () => {
    try {
      setLoading(true);
      const response = await axios.get('http://192.168.50.43:5002/api/extra-programs');
      const data = extractArrayFromResponse<ExtraProgram>(response.data);
      setPrograms(data);
    } catch (error) {
      console.error('Ошибка загрузки:', error);
      setSnackbar({ open: true, message: 'Ошибка загрузки данных', severity: 'error' });
      setPrograms([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPrograms();
  }, []);

  const handleToggleActive = async (program: ExtraProgram) => {
    const currentStatus = toBoolean(program.is_active);
    const newStatus = !currentStatus;
    
    try {
      await axios.patch(`http://192.168.50.43:5002/api/extra-programs/${program.id}`, {
        is_active: newStatus ? 1 : 0
      });
      
      setPrograms(prev => prev.map(p => 
        p.id === program.id ? { ...p, is_active: newStatus } : p
      ));
      
      setSnackbar({
        open: true,
        message: `Статус изменён на ${newStatus ? 'Активна' : 'Неактивна'}`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Ошибка обновления статуса:', error);
      setSnackbar({ open: true, message: 'Ошибка изменения статуса', severity: 'error' });
    }
  };

  const handleSave = async () => {
    if (!formData.name) {
      setSnackbar({ open: true, message: 'Введите название', severity: 'error' });
      return;
    }
    try {
      if (editingProgram) {
        await axios.put(`http://192.168.50.43:5002/api/extra-programs/${editingProgram.id}`, formData);
        setSnackbar({ open: true, message: 'Программа обновлена', severity: 'success' });
      } else {
        await axios.post('http://192.168.50.43:5002/api/extra-programs', formData);
        setSnackbar({ open: true, message: 'Программа создана', severity: 'success' });
      }
      setOpenDialog(false);
      loadPrograms();
    } catch (error) {
      setSnackbar({ open: true, message: 'Ошибка сохранения', severity: 'error' });
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Удалить программу?')) return;
    try {
      await axios.delete(`http://192.168.50.43:5002/api/extra-programs/${id}`);
      setSnackbar({ open: true, message: 'Программа удалена', severity: 'success' });
      loadPrograms();
    } catch (error) {
      setSnackbar({ open: true, message: 'Ошибка удаления', severity: 'error' });
    }
  };

  const handleRequestSort = (property: SortableKey) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedPrograms = [...programs].sort((a, b) => {
    let aValue: any = a[orderBy];
    let bValue: any = b[orderBy];
    
    if (orderBy === 'is_active') {
      aValue = toBoolean(a.is_active) ? 1 : 0;
      bValue = toBoolean(b.is_active) ? 1 : 0;
    }
    
    if (typeof aValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }
    
    if (aValue < bValue) return order === 'asc' ? -1 : 1;
    if (aValue > bValue) return order === 'asc' ? 1 : -1;
    return 0;
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const isVisible = (key: string) => visibleColumns.includes(key);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<Refresh />} onClick={loadPrograms} size="small">Обновить</Button>
          <Button variant="contained" startIcon={<Add />} onClick={() => { setEditingProgram(null); setFormData({ name: '', rate: 0, is_active: true }); setOpenDialog(true); }}>Добавить</Button>
        </Box>
      </Box>

      <Paper sx={{ p: 2, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <ColumnToggle
            columns={columns}
            storageKey="extra_programs_visible_columns"
            onChange={(cols) => setVisibleColumns(cols)}
          />
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                {isVisible('id') && (
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'id'}
                      direction={orderBy === 'id' ? order : 'asc'}
                      onClick={() => handleRequestSort('id')}
                      sx={{ fontWeight: 'bold' }}
                    >
                      ID
                    </TableSortLabel>
                  </TableCell>
                )}
                {isVisible('name') && (
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'name'}
                      direction={orderBy === 'name' ? order : 'asc'}
                      onClick={() => handleRequestSort('name')}
                      sx={{ fontWeight: 'bold' }}
                    >
                      Название
                    </TableSortLabel>
                  </TableCell>
                )}
                {isVisible('rate') && (
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'rate'}
                      direction={orderBy === 'rate' ? order : 'asc'}
                      onClick={() => handleRequestSort('rate')}
                      sx={{ fontWeight: 'bold' }}
                    >
                      Ставка
                    </TableSortLabel>
                  </TableCell>
                )}
                {isVisible('is_active') && (
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'is_active'}
                      direction={orderBy === 'is_active' ? order : 'asc'}
                      onClick={() => handleRequestSort('is_active')}
                      sx={{ fontWeight: 'bold' }}
                    >
                      Статус
                    </TableSortLabel>
                  </TableCell>
                )}
                <TableCell align="right">Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedPrograms.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography color="text.secondary" sx={{ py: 2 }}>Нет программ</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sortedPrograms.map((program) => {
                  const isActive = toBoolean(program.is_active);
                  return (
                    <TableRow 
                      key={program.id} 
                      hover
                      sx={{ 
                        opacity: isActive ? 1 : 0.6,
                        bgcolor: isActive ? 'inherit' : '#f5f5f5',
                        '&:hover': {
                          bgcolor: isActive ? 'action.hover' : '#e8e8e8',
                        }
                      }}
                    >
                      {isVisible('id') && <TableCell>{program.id}</TableCell>}
                      {isVisible('name') && <TableCell>{program.name}</TableCell>}
                      {isVisible('rate') && <TableCell>{program.rate} ₽/час</TableCell>}
                      {isVisible('is_active') && (
                        <TableCell>
                          <Switch
                            checked={isActive}
                            onChange={() => handleToggleActive(program)}
                            color="success"
                            size="small"
                          />
                        </TableCell>
                      )}
                      <TableCell align="right">
                        <Tooltip title="Редактировать">
                          <IconButton size="small" color="primary" onClick={() => { setEditingProgram(program); setFormData({ name: program.name, rate: program.rate, is_active: isActive }); setOpenDialog(true); }}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Удалить">
                          <IconButton size="small" color="error" onClick={() => handleDelete(program.id)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Modal
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        title={editingProgram ? 'Редактирование программы' : 'Новая программа'}
        actions={
          <>
            <Button 
              onClick={() => setOpenDialog(false)} 
              variant="outlined"
              sx={{ 
                borderRadius: 2,
                px: 3,
                py: 1,
                textTransform: 'none',
                fontWeight: 500,
              }}
            >
              Отмена
            </Button>
            <Button 
              onClick={handleSave} 
              variant="contained"
              sx={{ 
                borderRadius: 2,
                px: 3,
                py: 1,
                textTransform: 'none',
                fontWeight: 500,
              }}
            >
              {editingProgram ? 'Обновить' : 'Создать'}
            </Button>
          </>
        }
      >
        <Box sx={{ pt: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
          <TextField 
            label="Название" 
            value={formData.name} 
            onChange={(e) => setFormData({ ...formData, name: e.target.value })} 
            fullWidth 
            required 
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                bgcolor: '#ffffff',
              }
            }}
          />
          <TextField 
            label="Ставка (₽/час)" 
            type="number" 
            value={formData.rate} 
            onChange={(e) => setFormData({ ...formData, rate: parseFloat(e.target.value) || 0 })} 
            fullWidth 
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                bgcolor: '#ffffff',
              }
            }}
          />
          <FormControlLabel 
            control={
              <Switch 
                checked={formData.is_active} 
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })} 
                color="success" 
              />
            } 
            label={formData.is_active ? 'Активна' : 'Неактивна'}
            sx={{
              '& .MuiFormControlLabel-label': {
                fontWeight: 500,
                color: formData.is_active ? '#2e7d32' : '#d32f2f',
              }
            }}
          />
        </Box>
      </Modal>

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default ExtraProgramsPage;
